import './ApplePay.css';

import {RequestFn, SendFn} from 'src/entries/setupMessages';
import {ApplePayButtonOptions} from 'src/shared/ApplePayTypes';
import type {AppConfig} from '../initialize';
import {initialize} from '../initialize';
import injectScript from './injectScript';

const APPLE_PAY_JS_URL =
  'https://applepay.cdn-apple.com/jsapi/v1.2.4/apple-pay-sdk.js';

const initializeButton = (
  send: SendFn<'ApplePay'>,
  request: RequestFn<'ApplePay'>,
  buttonOptions: ApplePayButtonOptions,
  merchantIdentifier: string,
  merchantUrl: string,
): void => {
  injectScript(
    APPLE_PAY_JS_URL,
    () => {
      // @ts-expect-error - Property 'ApplePayWebOptions' does not exist on type 'Window & typeof globalThis'.
      const {ApplePaySession: applePaySession, ApplePayWebOptions: options} =
        window;
      // If neither loads in properly just return
      if (!applePaySession || !options) {
        return;
      }

      options.set({
        renderApplePayCodeAs: 'window',
        merchantUrl,
        onApplePayCodeClose: () => {
          send({event: 'onClose'});
        },
      });

      const setupSession = (session: ApplePaySession) => {
        const onValidateMerchant = (
          applePayEvent: ApplePayJS.ApplePayValidateMerchantEvent,
        ) => {
          request({
            event: 'onValidateMerchant',
            validationUrl: applePayEvent.validationURL,
          }).then((response) => {
            if (response.event === 'merchantValidation') {
              session.completeMerchantValidation(response.merchantSession);
            }
          });
        };

        const onPaymentMethodSelected = (
          applePayEvent: ApplePayJS.ApplePayPaymentMethodSelectedEvent,
        ) => {
          request({
            event: 'onPaymentMethodChange',
            paymentMethod: applePayEvent.paymentMethod,
          }).then((response) => {
            if (response.event === 'paymentMethodUpdate') {
              session.completePaymentMethodSelection(
                response.paymentMethodUpdate,
              );
            }
          });
        };

        const onShippingMethodSelected = (
          applePayEvent: ApplePayJS.ApplePayShippingMethodSelectedEvent,
        ) => {
          request({
            event: 'onShippingMethodChange',
            shippingMethod: applePayEvent.shippingMethod,
          }).then((response) => {
            if (response.event === 'shippingMethodUpdate') {
              session.completeShippingMethodSelection(
                response.shippingMethodUpdate,
              );
            } else if (response.event === 'rejectShippingMethodUpdate') {
              // https://git.corp.stripe.com/stripe-internal/pay-server/blob/62aef87851d8e48da365e084269a5001c5e0bf3d/stripe-js-v3/src/stripeJs/elements/implementations/express_checkout/applePayHelpers.ts#L894-L900
              session.completeShippingMethodSelection(
                ApplePaySession.STATUS_FAILURE,
                response.newTotal,
                [],
              );
            }
          });
        };

        const onShippingContactSelected = (
          applePayEvent: ApplePayJS.ApplePayShippingContactSelectedEvent,
        ) => {
          request({
            event: 'onShippingAddressChange',
            shippingContact: applePayEvent.shippingContact,
          }).then((response) => {
            if (response.event === 'shippingAddressUpdate') {
              session.completeShippingContactSelection({
                ...response.shippingAddressUpdate,
                errors: response.error
                  ? [
                      new ApplePayError(
                        response.error.errorCode,
                        response.error.errorField,
                      ),
                    ]
                  : undefined,
              });
            }
          });
        };

        const onCouponCodeChanged = (
          applePayEvent: ApplePayJS.ApplePayCouponCodeChangedEvent,
        ) => {
          request({
            event: 'onCouponCodeChange',
            couponCode: applePayEvent.couponCode,
          }).then((response) => {
            if (response.event === 'couponCodeUpdate') {
              session.completeCouponCodeChange(response.couponCodeUpdate);
            }
          });
        };

        const onPaymentAuthorized = (
          applePayEvent: ApplePayJS.ApplePayPaymentAuthorizedEvent,
        ) => {
          request({
            event: 'onPaymentAuthorized',
            payment: applePayEvent.payment,
          }).then((response) => {
            if (response.event === 'paymentAuthorization') {
              session.completePayment({
                ...response.result,
                errors: response.error
                  ? [
                      new ApplePayError(
                        response.error.errorCode,
                        response.error.errorField,
                      ),
                    ]
                  : undefined,
              });
            }
          });
        };

        const onCancel = (_applePayEvent: ApplePayJS.Event) => {
          send({event: 'onClose'});
        };

        session.onvalidatemerchant = onValidateMerchant;
        session.onpaymentmethodselected = onPaymentMethodSelected;
        session.onshippingmethodselected = onShippingMethodSelected;
        session.onshippingcontactselected = onShippingContactSelected;
        session.oncouponcodechanged = onCouponCodeChanged;
        session.onpaymentauthorized = onPaymentAuthorized;
        session.oncancel = onCancel;
      };

      const clickHandler = () => {
        request({event: 'onClick'}).then((response) => {
          if (response.event === 'resolveClick') {
            const session = new ApplePaySession(
              response.applePayVersion,
              response.paymentRequest,
            );

            setupSession(session);

            session.begin();
          }
        });
      };

      const button = document.getElementById('apple-pay-button');
      if (!button) {
        return;
      }
      button.onclick = clickHandler;

      // Set Apple Pay button attributes
      button.setAttribute('buttonstyle', buttonOptions.buttonStyle);
      button.setAttribute('type', buttonOptions.buttonType);
      button.setAttribute('locale', buttonOptions.buttonLocale);

      window.addEventListener('focus', () => {
        send({event: 'onFocus'});
      });

      window.addEventListener('blur', () => {
        send({event: 'onBlur'});
      });

      ApplePaySession.applePayCapabilities(merchantIdentifier).then(
        (statusResponse) => {
          send({
            event: 'canMakePayment',
            capabilityStatus: statusResponse.paymentCredentialStatus,
          });
        },
      );
    },
    () => {
      send({
        event: 'onError',
        error: 'Error loading Apple Pay JS script',
      });
    },
  );
};

const app = (config: AppConfig<'ApplePay'>) => {
  config.subscribe((messageData) => {
    if (messageData.event === 'loadButton') {
      initializeButton(
        config.send,
        config.request,
        messageData.buttonOptions,
        messageData.merchantIdentifier,
        messageData.merchantUrl,
      );
    }
  });
};

initialize({
  app,
  owner: 'buy-now-pay-later',
});
